import { useState, type FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahbthroh653ivfvnqtg";
const MAX_LENGTH = 500;

type Status = "idle" | "submitting" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [message, setMessage] = useState("");
  const [length, setLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("感谢你的留言，我们已经收到。");
      form.reset();
      setLength(0);
      return;
    }
    formData.delete("website_alt");

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      body.append(key, String(value));
    });

    setStatus("submitting");
    setMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMessage =
        (meta.message as string) ||
        (parsed?.message as string) ||
        (meta.detail as string) ||
        responseText;
      const code = parsed?.code as string | undefined;
      const isSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setMessage(
          typeof serverMessage === "string" && serverMessage.length > 0
            ? serverMessage
            : "提交失败，请稍后重试。",
        );
        return;
      }

      setStatus("success");
      setMessage("提交成功，感谢你的留言，我们会在 24 小时内回复。");
      form.reset();
      setLength(0);
    } catch {
      setStatus("error");
      setMessage("网络异常导致提交失败，请检查网络后重试。");
    }
  };

  return (
    <form
      id="contact-form-guatian"
      data-readdy-form
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8"
    >
      <div className="flex flex-col gap-2">
        <label htmlFor="contact-name" className="text-sm font-label text-foreground-800">
          你的称呼 <span className="text-primary-600">*</span>
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          placeholder="怎么称呼你"
          className="h-11 rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="contact-email" className="text-sm font-label text-foreground-800">
          邮箱 <span className="text-primary-600">*</span>
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          placeholder="用于接收回复的邮箱"
          className="h-11 rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="contact-topic" className="text-sm font-label text-foreground-800">
          联系主题
        </label>
        <select
          id="contact-topic"
          name="topic"
          defaultValue="选题建议"
          className="h-11 cursor-pointer rounded-lg border border-background-300 bg-background-50 px-4 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
        >
          <option value="选题建议">选题建议</option>
          <option value="内容纠错">内容纠错</option>
          <option value="合作咨询">合作咨询</option>
          <option value="其他问题">其他问题</option>
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <label htmlFor="contact-message" className="text-sm font-label text-foreground-800">
          留言内容 <span className="text-primary-600">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          rows={5}
          maxLength={MAX_LENGTH}
          onChange={(event) => setLength(event.target.value.length)}
          placeholder="请简单描述你想反馈的内容或希望看到的选题方向"
          className="resize-none rounded-lg border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
        />
        <span className="text-right text-xs text-foreground-400">
          {length} / {MAX_LENGTH}
        </span>
      </div>

      <div className="form-honeypot-field" aria-hidden="true">
        <label htmlFor="contact-website-alt">Website</label>
        <input
          id="contact-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status !== "idle" && status !== "submitting" && message && (
        <p
          role="status"
          className={`flex items-start gap-2 rounded-lg px-4 py-3 text-sm ${
            status === "success"
              ? "bg-secondary-100 text-secondary-900"
              : "bg-primary-100 text-primary-800"
          }`}
        >
          <i
            className={status === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"}
            aria-hidden="true"
          />
          {message}
        </p>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="flex h-12 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-sm font-label text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
      >
        <i className={status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} aria-hidden="true" />
        {status === "submitting" ? "正在提交…" : "提交留言"}
      </button>

      <p className="text-xs leading-relaxed text-foreground-400">
        提交即表示你同意我们使用该信息回复你的咨询。我们不会将信息用于其他用途。
      </p>
    </form>
  );
}